import { SelectInput, SelectInputItem } from '@ads/components-react';
import { LinkIcon } from '@heroicons/react/24/outline';
import { useEffect, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePlanEdit } from '../../../context/PlanEditContext';
import {
  useCourseFinancialInfo,
  useCourseRegistrationFinancials,
  usePersonCourseRegistrations,
} from '../../../hooks/usePlanCreateLookups';
import { parseStudentKeyId } from '../../../utils/studentSelection';
import { StudentAutocomplete } from '../LookupAutocomplete';
import { PlanSectionCard } from '../PlanSectionCard';

/**
 * Converte a chave textual do lookup de pessoa no id numérico do backend.
 *
 * `person/shared/people-multiple` devolve `"USR260953"`; o lookup de matrículas
 * espera `260953`. `Number('USR260953')` é `NaN`, e foi assim que o filtro da
 * listagem perdeu o parâmetro em silêncio uma vez — daí a extração explícita.
 *
 * @param personKey - Chave crua vinda do autocomplete.
 * @returns Id numérico, ou `null` quando a chave não traz um.
 */
function toPersonId(personKey: string | undefined): number | null {
  if (personKey === undefined || personKey === '') return null;
  const digits = parseStudentKeyId(personKey);
  return digits === '' ? null : Number(digits);
}

/**
 * Bloco de vínculo do formulário de criação de plano.
 *
 * Os três campos — pessoa, matrícula e plano de pagamento — são encadeados: cada
 * um só habilita o seguinte, e trocar um deles invalida os que dependiam dele.
 * A invalidação acontece no `onChange`, e não num efeito sobre o valor: um
 * efeito dispararia também no `reset` inicial e apagaria seleções válidas.
 *
 * Escolher o plano de pagamento pré-preenche valor total, valor de matrícula e
 * desconto pontual com os valores do plano do curso. É sugestão — os campos
 * seguem editáveis.
 */
/** Props de `PlanLinkSection`. */
export interface PlanLinkSectionProps {
  /**
   * Nome da pessoa já escolhida na origem.
   *
   * Quando a criação é aberta pelo menu de um contrato, o `personId` chega pelo
   * formulário mas o rótulo não — ele não faz parte do payload. Sem isto o campo
   * apareceria vazio apesar de preenchido, e o operador reescolheria a pessoa.
   */
  readonly initialPersonLabel?: string | undefined;
}

export function PlanLinkSection({ initialPersonLabel }: PlanLinkSectionProps = {}) {
  const { t } = useTranslation('individualPayment');
  const { form, isReadOnly } = usePlanEdit();
  const { control, watch, setValue, formState } = form;

  /**
   * Rótulo da pessoa selecionada.
   *
   * Fica fora do formulário porque é puramente de exibição: o backend recebe o
   * `courseRegistrationId`, nunca o nome, e guardá-lo no payload seria ruído no
   * contrato.
   */
  const [personLabel, setPersonLabel] = useState(initialPersonLabel ?? '');

  const personKey = watch('personId') as string | undefined;
  const courseRegistrationId = watch('courseRegistrationId') as number | undefined;
  const courseFinancialId = watch('courseFinancialId') as number | undefined;

  const personId = toPersonId(personKey);
  const { courseRegistrations } = usePersonCourseRegistrations(personId);
  const { courseFinancials } = useCourseRegistrationFinancials(courseRegistrationId ?? null);
  const { info } = useCourseFinancialInfo(courseFinancialId ?? null);

  /**
   * Último plano cujos valores já foram aplicados.
   *
   * Sem isso o pré-preenchimento voltaria a rodar a cada render em que `info`
   * fosse a mesma referência do cache, desfazendo o que o operador digitou
   * depois de escolher o plano.
   */
  const appliedFinancialId = useRef<number | null>(null);

  // Efeito legítimo: reage à **chegada da resposta**, não a uma ação do operador.
  useEffect(() => {
    if (courseFinancialId === undefined || info === null) return;
    if (appliedFinancialId.current === courseFinancialId) return;

    appliedFinancialId.current = courseFinancialId;
    setValue('totalValue', info.totalValue ?? 0, { shouldDirty: true });
    setValue('valueRegistration', info.valueRegistration, { shouldDirty: true });
    setValue('discountPunctualPercentage', info.discountPunctualPercentage, { shouldDirty: true });
  }, [courseFinancialId, info, setValue]);

  /** Mensagens de validação do bloco, resolvidas a partir das chaves do schema. */
  const message: Record<string, string> = {
    'planCreate.validation.personRequired': t('planCreate.validation.personRequired'),
    'planCreate.validation.enrollmentRequired': t('planCreate.validation.enrollmentRequired'),
    'planCreate.validation.paymentPlanRequired': t('planCreate.validation.paymentPlanRequired'),
  };

  /**
   * Traduz a mensagem de erro do campo.
   *
   * O schema guarda **chaves** de i18n, não texto — assim a validação continua
   * verificável sem montar a UI. A resolução acontece aqui.
   */
  const errorText = (key: string | undefined): string | undefined =>
    key === undefined ? undefined : (message[key] ?? key);

  const personError = errorText(formState.errors.personId?.message);
  const enrollmentError = errorText(formState.errors.courseRegistrationId?.message);
  const paymentPlanError = errorText(formState.errors.courseFinancialId?.message);

  return (
    <PlanSectionCard title={t('planCreate.link.title')} icon={LinkIcon}>
      <div className="grid grid-cols-1 gap-xxxs md:grid-cols-3">
        <Controller
          control={control}
          name="personId"
          render={({ field }) => (
            <div className="flex flex-col gap-quark">
              <StudentAutocomplete
                label={`${t('planCreate.link.person')} *`}
                placeholder={t('planCreate.link.personPlaceholder')}
                value={field.value ?? ''}
                selectedLabel={personLabel}
                onFreeTextChange={() => {
                  // Pessoa aqui não é filtro de texto livre: sem item escolhido não
                  // há matrícula a carregar. O texto digitado só alimenta a busca.
                }}
                onChange={(key, label) => {
                  field.onChange(key);
                  setPersonLabel(label);
                  // Matrícula e plano pertenciam à pessoa anterior.
                  setValue('courseRegistrationId', undefined, { shouldDirty: true });
                  setValue('courseFinancialId', undefined, { shouldDirty: true });
                  appliedFinancialId.current = null;
                }}
              />
              {personError ? (
                <span className="ts-paragraph-xxs text-base-danger-700">{personError}</span>
              ) : null}
            </div>
          )}
        />

        <Controller
          control={control}
          name="courseRegistrationId"
          render={({ field }) => (
            <SelectInput
              label={`${t('planCreate.link.enrollment')} *`}
              size="md"
              fullWidth
              placeholder={t('planCreate.link.selectPlaceholder')}
              disabled={isReadOnly || personId === null}
              hasError={enrollmentError !== undefined}
              {...(enrollmentError === undefined ? {} : { helper: enrollmentError })}
              value={field.value ? String(field.value) : ''}
              onValueChange={value => {
                field.onChange(Number(value));
                // O plano de pagamento pertencia à matrícula anterior.
                setValue('courseFinancialId', undefined, { shouldDirty: true });
                appliedFinancialId.current = null;
              }}
            >
              {courseRegistrations.map(option => (
                <SelectInputItem
                  custom={{ root: 'text-nowrap' }}
                  key={String(option.id)}
                  value={String(option.id)}
                >
                  {option.label}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />

        <Controller
          control={control}
          name="courseFinancialId"
          render={({ field }) => (
            <SelectInput
              label={`${t('planCreate.link.paymentPlan')} *`}
              size="md"
              fullWidth
              placeholder={t('planCreate.link.selectPlaceholder')}
              disabled={isReadOnly || courseRegistrationId === undefined}
              hasError={paymentPlanError !== undefined}
              {...(paymentPlanError === undefined ? {} : { helper: paymentPlanError })}
              value={field.value ? String(field.value) : ''}
              onValueChange={value => field.onChange(Number(value))}
            >
              {courseFinancials.map(option => (
                <SelectInputItem
                  custom={{ root: 'text-nowrap' }}
                  key={String(option.id)}
                  value={String(option.id)}
                >
                  {option.label}
                </SelectInputItem>
              ))}
            </SelectInput>
          )}
        />
      </div>
    </PlanSectionCard>
  );
}
